import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { BatchRun } from '../viewThruBatchView/shared'
import { AnalysisClient, FlaggedClient, Outcome, ViewThruBatchAnalysis } from '../viewThruBatchView/BatchAnalysis'
import { CcjRule, DEFAULT_CCJ_RULE } from './ccjRules'

/** Every completed batch, newest first — the page analyses these. */
const BATCH_LIST_LIMIT = 500
/** Analyses run against the introducer databases, so keep the fan-out small. */
const ANALYSIS_CONCURRENCY = 2

export type BatchAnalysisState =
    | { status: 'idle' }
    | { status: 'running' }
    | { status: 'complete'; analysis: ViewThruBatchAnalysis; analysedAt: Date }
    | { status: 'failed'; error: string }

/** A client row tagged with the batch it was analysed in, for cross-batch tables and exports. */
export type BatchFlaggedClient = FlaggedClient & {
    batchId: string
    batchLabel: string
    batchOrigin: string
}

export const batchLabel = (run: BatchRun) => run.filename || run.id

export const flagBatchClients = (run: BatchRun, analysis: ViewThruBatchAnalysis): BatchFlaggedClient[] => {
    const tag =
        (outcome: Outcome) =>
        (client: AnalysisClient): BatchFlaggedClient => ({
            ...client,
            outcome,
            batchId: run.id,
            batchLabel: batchLabel(run),
            batchOrigin: run.origin || analysis.params.origin || '',
        })
    return [
        ...analysis.eligibleClients.map(tag('eligible')),
        ...analysis.ineligibleClients.map(tag('ineligible')),
        ...(analysis.duplicateClients || analysis.notEligibleDups || []).map(tag('duplicate')),
    ]
}

export const useAllBatchesAnalysis = () => {
    const [batches, setBatches] = useState<BatchRun[]>([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [states, setStates] = useState<Record<string, BatchAnalysisState>>({})
    const [running, setRunning] = useState(false)
    const [ccjRule, setCcjRuleState] = useState<CcjRule>(DEFAULT_CCJ_RULE)
    const cancelRef = useRef(false)

    /**
     * Results analysed under one CCJ rule say nothing about the other, so
     * switching rules throws every result away; the batches must be analysed
     * again before the page shows anything.
     */
    const setCcjRule = useCallback(
        (rule: CcjRule) => {
            if (rule === ccjRule || running) return
            setCcjRuleState(rule)
            setStates({})
        },
        [ccjRule, running]
    )

    const loadBatches = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api({ url: 'viewthru-batches', data: { limit: BATCH_LIST_LIMIT } })
            if (!response.data?.success) throw new Error(response.data?.message || 'Could not load batches.')
            const complete = (response.data.batches as BatchRun[]).filter((run) => run.status === 'complete')
            setBatches(complete)
            // Default to everything that can be analysed; a re-load keeps any
            // narrower selection the user already made.
            setSelected((current) =>
                current.size
                    ? new Set(complete.map((run) => run.id).filter((id) => current.has(id)))
                    : new Set(complete.map((run) => run.id))
            )
            setLoadError(null)
        } catch (error: any) {
            setBatches([])
            setLoadError(error?.response?.data?.message || error?.message || 'Could not load batches.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void loadBatches()
    }, [loadBatches])

    const setState = (batchId: string, state: BatchAnalysisState) =>
        setStates((current) => ({ ...current, [batchId]: state }))

    const analyseOne = useCallback(async (batchId: string) => {
        setState(batchId, { status: 'running' })
        try {
            const response = await api({ url: 'viewthru-batch-analysis', data: { batchId, ccjRule } })
            if (!response.data?.success) throw new Error(response.data?.message || 'Could not analyse this batch.')
            const analysis = response.data as ViewThruBatchAnalysis
            setState(batchId, { status: 'complete', analysis, analysedAt: new Date() })
            if (response.data.batch) {
                setBatches((current) =>
                    current.map((run) => (run.id === batchId ? { ...run, ...response.data.batch } : run))
                )
            }
        } catch (error: any) {
            setState(batchId, {
                status: 'failed',
                error: error?.response?.data?.message || error?.message || 'Could not analyse this batch.',
            })
        }
    }, [ccjRule])

    /** Analyse the selected batches, a couple at a time, in the order they are listed. */
    const analyseSelected = useCallback(
        async (ids?: string[]) => {
            const queue = (ids || batches.map((run) => run.id).filter((id) => selected.has(id))).slice()
            if (!queue.length || running) return
            cancelRef.current = false
            setRunning(true)
            const workers = Array.from({ length: Math.min(ANALYSIS_CONCURRENCY, queue.length) }, async () => {
                while (queue.length && !cancelRef.current) {
                    const next = queue.shift()
                    if (next) await analyseOne(next)
                }
            })
            await Promise.all(workers)
            setRunning(false)
        },
        [analyseOne, batches, running, selected]
    )

    const cancel = () => {
        cancelRef.current = true
    }

    const toggle = (batchId: string) =>
        setSelected((current) => {
            const next = new Set(current)
            if (next.has(batchId)) next.delete(batchId)
            else next.add(batchId)
            return next
        })

    const selectAll = (on: boolean) => setSelected(on ? new Set(batches.map((run) => run.id)) : new Set())

    const analysed = useMemo(
        () =>
            batches
                .map((run) => {
                    const state = states[run.id]
                    return state?.status === 'complete' ? { run, analysis: state.analysis, analysedAt: state.analysedAt } : null
                })
                .filter((entry): entry is { run: BatchRun; analysis: ViewThruBatchAnalysis; analysedAt: Date } => Boolean(entry)),
        [batches, states]
    )

    const allClients = useMemo(
        () => analysed.flatMap(({ run, analysis }) => flagBatchClients(run, analysis)),
        [analysed]
    )

    const progress = useMemo(() => {
        const ids = batches.map((run) => run.id).filter((id) => selected.has(id))
        const done = ids.filter((id) => {
            const status = states[id]?.status
            return status === 'complete' || status === 'failed'
        }).length
        const failed = ids.filter((id) => states[id]?.status === 'failed').length
        return { total: ids.length, done, failed, percent: ids.length ? Math.round((done / ids.length) * 100) : 0 }
    }, [batches, selected, states])

    return {
        batches,
        loading,
        loadError,
        reload: loadBatches,
        selected,
        toggle,
        selectAll,
        states,
        running,
        ccjRule,
        setCcjRule,
        analyseSelected,
        analyseOne,
        cancel,
        analysed,
        allClients,
        progress,
    }
}
